/**
 * Boss system - HP, damage, reward calculations
 * Headless - no GUI
 */

import type { GameState, BossEvent } from '../state.js';
import type { BossDef, BossReward } from '../data/boss.js';
import { BOSS_DEFS } from '../data/boss.js';

/** Calculate boss HP based on current CPS and multiplier */
export function calcBossHp(cps: number, multiplier: number): number {
  const base = cps * multiplier;
  return Math.max(base, 100); // minimum 100 HP
}

/** Calculate tap damage against boss */
export function calcTapDamage(tapPower: number): number {
  return tapPower * 0.5;
}

/** Calculate boss kill reward */
export function calcBossReward(
  _boss: BossEvent,
  def: BossDef,
  cps: number,
  streak: number,
): BossReward {
  // +10% per streak, capped at 2x (streak 10)
  const streakMult = Math.min(1 + streak * 0.1, 2);

  switch (def.rewardType) {
    case 'ip_burst':
      return { ip: cps * 60 * streakMult, docs: 0, gc: 0, boostSeconds: 0 };
    case 'docs_bonus':
      return { ip: 0, docs: Math.ceil(5 * streakMult), gc: 0, boostSeconds: 0 };
    case 'cps_boost':
      return { ip: 0, docs: 0, gc: 0, boostSeconds: 60 };
    case 'gc_bonus':
      return { ip: 0, docs: 0, gc: Math.ceil(10 * streakMult), boostSeconds: 0 };
  }
}

/** Calculate next boss spawn delay (10~20 min base) */
export function calcNextBossDelay(_state: GameState): number {
  const baseMin = 10 * 60_000;
  const baseMax = 20 * 60_000;
  // Future: faction bonus can reduce this
  return baseMin + Math.random() * (baseMax - baseMin);
}

/** Get all bosses available based on current game state */
export function getAvailableBosses(state: GameState): BossDef[] {
  return BOSS_DEFS.filter(def => {
    const cond = def.unlockCondition;
    switch (cond.type) {
      case 'start':
        return true;
      case 'prestige':
        return state.prestigeCount >= cond.count;
      case 'conquered':
        return state.conquest.countries[cond.countryId]?.conquered === true;
    }
  });
}

/** Pick a random boss from available pool */
export function pickRandomBoss(state: GameState): BossDef | null {
  const available = getAvailableBosses(state);
  if (available.length === 0) return null;
  const idx = Math.floor(Math.random() * available.length);
  return available[idx];
}
